import { ReactNode } from 'react';
import { useTooltip } from './Tooltip';
import { GLOSSARY, type GlossaryKey } from '../../lib/glossary';

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  /** Small count badge shown after the label. */
  count?: number;
  /** `danger` = the Student 360 `.tn` badge (danger-bg / danger-ink, unchanged when active). */
  countTone?: 'danger';
  hidden?: boolean;
  groupBefore?: boolean;
  /** Glossary key or inline text explained on hover/focus of the tab. */
  help?: GlossaryKey | string;
}

interface Props<K extends string = string> {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  right?: ReactNode;
  /** Accessible name for the tablist. */
  label?: string;
  /** Exam-family strip (`.tabs` ExamList): 44px tall, 14px side padding, 3px accent underline, 18px below. */
  accent?: boolean;
  /**
   * Class performance `.tabs`: the strip is the first child of a PanelCard (22px padding) and
   * bleeds to its side edges (`margin:-8px -22px 24px; padding:0 12px`), tabs padded 16/12/13.
   */
  inCard?: boolean;
  /** Admin strip with the 3px accent underline (Cases composer, Parent digest history `.tabs button`). */
  thick?: boolean;
  /** Extra classes on the strip, for the small per-page margin overrides (`margin:0 0 14px`). */
  className?: string;
}

type Variant = 'underline' | 'accent' | 'inCard';

const STRIP: Record<Variant, string> = {
  // Admin `.tabs`: gap 4, line rule, 16px below.
  underline: 'mb-4 gap-2',
  accent: 'mb-[18px] gap-2',
  inCard: '-mx-[22px] -mt-2 mb-6 gap-2 px-3',
};

const LIST_GAP: Record<Variant, string> = { underline: 'gap-1', accent: 'gap-1', inCard: 'gap-0.5' };

// rounded-none: the legacy global button rule gives every button a 12px radius, which would bend
// the underline up at both ends; the design's tab underline is a straight bar.
const BUTTON: Record<Variant, string> = {
  // Admin: min-h 42, px 12, 2px accent underline, -1px overlap with the rule.
  underline: '-mb-px min-h-[42px] px-3 border-b-2',
  accent: '-mb-px min-h-[44px] px-3.5 border-b-[3px]',
  inCard: 'px-3 pb-[13px] pt-4 border-b-[3px]',
};

function helpText(help?: GlossaryKey | string): string | undefined {
  if (!help) return undefined;
  return help in GLOSSARY ? GLOSSARY[help as GlossaryKey].def : help;
}

function TabButton({ active, label, count, countTone, help, variant, thick, onClick }: { variant: Variant; thick?: boolean; active: boolean; label: string; count?: number; countTone?: 'danger'; help?: GlossaryKey | string; onClick: () => void }) {
  const { ref, triggerProps, tooltip } = useTooltip<HTMLButtonElement>(helpText(help));
  return (
    <>
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={active}
        onClick={onClick}
        {...(help ? triggerProps : {})}
        className={`inline-flex items-center gap-2 whitespace-nowrap rounded-none bg-transparent text-sm font-semibold transition-colors ${BUTTON[variant]}${thick && variant === 'underline' ? ' !border-b-[3px]' : ''} ${
          active ? 'border-accent text-ink' : 'border-transparent text-muted hover:text-ink'}`}>
        {label}
        {count !== undefined && (
          <>
          <span className="sr-only">{`, ${count}`}</span>
          <span data-testid="tab-count" aria-hidden="true"
            className={`rounded-full px-[7px] py-0.5 font-mono text-[11px] font-semibold ${
              countTone === 'danger' ? 'bg-danger-bg text-danger-ink'
                : active ? 'bg-navy text-white' : `bg-ground ${variant === 'underline' ? 'text-muted' : 'text-muted-2'}`}`}>{count}</span>
          </>
        )}
      </button>
      {tooltip}
    </>
  );
}

export default function Tabs<K extends string = string>({ items, value, onChange, right, label, accent, inCard, thick, className = '' }: Props<K>) {
  const variant: Variant = inCard ? 'inCard' : accent ? 'accent' : 'underline';
  const visible = items.filter((t) => !t.hidden);
  return (
    <div data-variant={variant} className={`flex flex-wrap items-stretch justify-between border-b border-line ${STRIP[variant]} ${className}`}>
      <div role="tablist" aria-label={label} className={`flex min-w-0 max-w-full flex-1 items-stretch overflow-x-auto [scrollbar-width:none] ${variant === 'inCard' ? '' : '-mb-px'} ${LIST_GAP[variant]}`}>
        {visible.map((t) => (
          <span key={t.key} className="flex items-stretch">
            {t.groupBefore && (
              // `.tsep`: a 1px line-coloured rule spanning the tab height minus 14px top and bottom.
              <span data-testid="tab-group-divider" aria-hidden
                className="mx-2 my-3.5 w-px flex-none bg-line" />
            )}
            <TabButton active={t.key === value} label={t.label} count={t.count} countTone={t.countTone} help={t.help} variant={variant} thick={thick} onClick={() => onChange(t.key)} />
          </span>
        ))}
      </div>
      {right && <div className="my-2 flex items-center gap-2">{right}</div>}
    </div>
  );
}
